// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services/authService';
import { userService } from '../services/userService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // This will automatically handle Google sign-in persistence
    const unsubscribe = authService.onAuthStateChange(async (user) => {
      try {
        if (user) {
          // User is signed in
          // Fetch user profile from Firestore
          let profile = await userService.getUser(user.uid);
          
          // If no profile exists, create one (shouldn't happen but safety check)
          if (!profile) {
            await userService.saveUser(user.uid, {
              name: user.displayName || '',
              email: user.email || '',
              mobile: '',
              roles: ['LENDER'],
              isVerified: false,
              emailVerified: user.emailVerified,
              createdAt: new Date().toISOString()
            });
            profile = await userService.getUser(user.uid);
          }
          
          setCurrentUser(user);
          setUserProfile(profile);
        } else {
          // User is signed out
          setCurrentUser(null);
          setUserProfile(null);
        }
      } catch (err) {
        console.error('Error in auth state change:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signUp = async (email, password, userData) => {
    try {
      setError(null);
      const result = await authService.signUpWithEmail(email, password, userData);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const signIn = async (email, password) => {
    try {
      setError(null);
      const result = await authService.signInWithEmail(email, password);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const signInWithGoogle = async (additionalData) => {
    try {
      setError(null);
      const result = await authService.signInWithGoogle(additionalData);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await authService.signOut();
      setCurrentUser(null);
      setUserProfile(null);
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const resendVerificationEmail = async () => {
    try {
      setError(null);
      return await authService.resendVerificationEmail();
    } catch (err) {
      setError(err);
      throw err;
    }
  };

  const refreshUserProfile = async () => {
    if (currentUser) {
      const profile = await userService.getUser(currentUser.uid);
      setUserProfile(profile);
      // Reload auth user to get latest emailVerified status
      await currentUser.reload();
      setCurrentUser(authService.getCurrentUser());
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resendVerificationEmail,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};